import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown } from "react-bootstrap";
import { Loader } from 'rsuite';
import "react-dropdown/style.css";
import "./OrderCreator.css";
import FeaturesBar from "./FeaturesBar/FeaturesBar.jsx";
import * as turf from '@turf/turf';
import { createOrders, deleteDraftOrder, getConstellations, getWeather, getWeatherBbox } from "../../services/orders.service.jsx";
import * as orderOptions from "../../common/constants/orderOptions.js";
import { getLabel, getValue } from "../../common/utils/helperFunctions.jsx";
import { resetUploadedFeatures, setSuccessMessage, resetSuccessMessage, setProvider, clearProvider, resetHighlightOrbitpath, resetEditOrder } from "../../slices/orderSlice.js";
import FileSvg from '../../assets/images/svgs/File.svg?react';
import { cleanUpDraw, resetCoordinatesGeometry, resetDisplayView, resetMode, setAOICoordinates, setGeometryDrawn, setOrderAoi } from "../../slices/mapSlice.js";
import { resetSelectedFeature } from "../../slices/searchSlice.js";
import SingleSelectDropdown from "../../common/utils/SingleSelectDropdown.jsx";
import Close from "../../assets/images/svgs/Close.svg?react"
import MinimizeIcon from "../../assets/images/svgs/MinimizeIcon.svg?react"
import MaximizeIcon from "../../assets/images/svgs/MaximizeIcon.svg?react"
import DropdownWhiteOpen from "../../assets/images/svgs/DropdownWhiteOpen.svg?react"
import DropdownWhiteClose from "../../assets/images/svgs/DropdownWhiteClose.svg?react"
import BrightGreenTickIcon from "../../assets/images/svgs/BrightGreenTickIcon.svg?react"
import HelpCircleIcon from "../../assets/images/svgs/HelpCircleIcon.svg?react"
import ConstellationTimeline from "./ConstellationTimeline/ConstellationTimeline.jsx";
import ErrorMessage from "./ErrorMessage/ErrorMessage.jsx";
import { useNavigate } from "react-router-dom";

const OrderCreation = ({ props }) => {

  const workspaceID = '272c97f6-8215-4071-8dab-f26b4ae2b9f6';

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const coordinates = useSelector(state => state.map.coordinates);
  const geometry = useSelector(state => state.map.geometry);
  const mode = useSelector(state => state.map.mode);
  const loading = useSelector(state => state.orderDetails.loading);
  const uploadedCollection = useSelector(state => state.orderDetails.uploadedCollection);
  const uploadedFilename = useSelector(state => state.orderDetails.uploadedFilename);
  const provider = useSelector(state => state.providerDetails.providers);
  const storageLocation = useSelector(state => state.providerDetails.storageEndpoints);
  const collections = useSelector(state => state.collectionDetails.collections);
  const providerConstellations = useSelector(state => state.orderDetails.providerConstellations);
  const filteredProviderConstellations = providerConstellations?.filter(item => item?.error !== "not-supported") || [];
  const showOrbit = useSelector(state => state.orderDetails.showOrbit);
  const editOrder = useSelector(state => state.orderDetails.editOrder);

  const providerOptions = provider.filter(p => p.providerType === 'StrataTest' || p.providerType === 'Antaris');

  const [isModalMinimized, setIsModalMinimized] = useState(false);

  const dbOptions = ["Every contact", "Every Hour", "Every 3 Hours", "Every 6 Hours"];

  const [orderCreationStage, setOrderCreationStage] = useState({ details: true, type: false, summary: false });

  const isFileUploaded = () => {
    return Object.keys(uploadedCollection).length !== 0
  };

  const [orderData, setOrderData] = useState({
    name: '',
    orderType: 'Standard Order',
    aoi: {},
    aoiLabel: '',
    priorityLane: orderOptions.priorityLane[0],
    imagingSpectrum: orderOptions.imagingSpectrum[0].label,
    coverageWindowValue: 1,
    coverageWindowValueType: orderOptions.coverageWindowValueType[1],
    isRecurring: false,
    isAdvancedDetails: false,
    gsdMin: 0.7,
    gsdMax: 100,
    nadirMin: -10,
    nadirMax: 10,
    sunAngleMin: 0,
    sunAngleMax: 90,
    cloudCoverMax: 80,
    selectedRecurringType: orderOptions.recurringType[0].value,
    defaultOption: dbOptions[0],
    provider: null,
    storageLocation: null,
    collections: null,
    resolution: orderOptions.resolution[0],
  });

  useEffect(() => {
    if (Object.keys(geometry).length > 0) {
      dispatch(clearProvider());
      
      setOrderData(prev => ({
        ...prev,
        provider: null
      }));
    }
  }, [geometry, dispatch]);

  useEffect(() => {
    if (showOrbit) {
      setOrderData(prev => ({
        ...prev,
        provider: showOrbit.providerID
      }));
    }
  }, [showOrbit]);  

  useEffect(() => {
    if (Object.keys(editOrder).length > 0) {
      const isAntaris = !!editOrder.antaris;
      const providerKey = isAntaris ? 'antaris' : 'strataTest';
      const providerData = editOrder[providerKey];
  
      if (providerData?.aoi) {
        // dispatch(setOrderAoi(providerData.aoi.geometry))
        dispatch(setGeometryDrawn(providerData.aoi.geometry));
        dispatch(setAOICoordinates(providerData.aoi.geometry.coordinates));
      }
  
      setOrderData(prev => ({
        ...prev,
        name: editOrder.name ?? prev.name,
        priorityLane: providerData?.priorityLane ?? prev.priorityLane,
        imagingSpectrum: 
           getLabel(orderOptions.imagingSpectrum, providerData?.imagingSpectrum)
           ?? prev.imagingSpectrum,
      
        gsdMin: providerData?.gsdMin ?? prev.gsdMin,
        gsdMax: providerData?.gsdMax ?? prev.gsdMax,
        nadirMin: providerData?.nadirAngleMin ?? prev.nadirMin,
        nadirMax: providerData?.nadirAngleMax ?? prev.nadirMax,
        sunAngleMin: providerData?.sunAngleMin ?? prev.sunAngleMin,
        sunAngleMax: providerData?.sunAngleMax ?? prev.sunAngleMax,
        cloudCoverMax: providerData?.cloudCoverMax ?? prev.cloudCoverMax,
      
        provider: editOrder.providerID ?? prev.provider,
        collections: prev.collections,
        storageLocation: editOrder.storageLocation ?? prev.storageLocation,
      }));
  
      setOrderCreationStage({ details: true, type: false, summary: false });
    }
  }, [editOrder]);

  // this should be removed, when we start accepting features
  const aoiCoordinates = isFileUploaded() ?
    uploadedCollection.features[0].geometry :
    geometry;

  const getDrawnFeature = () => {
    let featureShape = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: '',
        coordinates,
      },
    };
    if (mode === 'draw_polygon') {
      featureShape.geometry.type = 'Polygon';
    } else if (mode === 'draw_point') {
      featureShape.geometry.type = 'Point';
    };
    return [featureShape];
  };

  const getFeatureCollection = () =>
    isFileUploaded() ? uploadedCollection.features : getDrawnFeature();

  const transformOrderData = (d) => {
    const selectedProvider = providerOptions.find((p) => p.providerID === d.provider);
    const providerKey = selectedProvider.providerType === "StrataTest" ? "strataTest" : "antaris";

    return {
      name: d.name,
      workspaceID,
      assets: [],
      [providerKey]: {
        aoi: aoiCoordinates,
        priorityLane: d.priorityLane,
        imagingSpectrum: getValue(orderOptions.imagingSpectrum, d.imagingSpectrum),
        gsdMin: parseInt(d.gsdMin),
        gsdMax: parseInt(d.gsdMax),
        nadirAngleMin: parseInt(d.nadirMin),
        nadirAngleMax: parseInt(d.nadirMax),
        sunAngleMin: parseInt(d.sunAngleMin),
        sunAngleMax: parseInt(d.sunAngleMax),
        cloudCoverMax: parseInt(d.cloudCoverMax),
        coverageWindow: `${d.coverageWindowValue} ${d.coverageWindowValueType}`,
        resolution: orderOptions.resolution[0],
        recurring: d.isRecurring,
        recurringType: getValue(orderOptions.recurringType, d.recurringType),
      },
      providerID: d.provider,
      collectionID: d.collections,
    };
  };

  const handleChange = (e, item) => {
    const { name, value } = e.target || e;
    setOrderData((prevData) => {
      return {
        ...prevData,
        [name]: typeof item !== 'undefined' ? item : value,
      };
    });
  };

  const fetchCentroidWeather = (isFallback) => {
    const center = turf.centroid(aoiCoordinates);
    const [longitude, latitude] = center.geometry.coordinates;
    dispatch(getWeather({ latitude, longitude, hourly: 'cloud_cover,temperature_2m' }, { isFallback }));
  };

  const runAnalysisSimulation = () => {
    dispatch(getConstellations({ aoi:  aoiCoordinates }));

    if (aoiCoordinates.type === 'Polygon') {
      const [minLongitude, minLatitude, maxLongitude, maxLatitude] = turf.bbox(aoiCoordinates);
      // backend bbox param order is [min_latitude, min_longitude, max_latitude, max_longitude]
      // the bbox grid request fails when the AOI is too big - fall back to the centre point
      dispatch(getWeatherBbox({
        bbox: JSON.stringify([minLatitude, minLongitude, maxLatitude, maxLongitude]),
      }, () => fetchCentroidWeather(true)));
    } else {
      fetchCentroidWeather(false);
    }
  }

  const showConstellation = (provider) => {
    setOrderData(prev => ({
      ...prev,
      provider: provider.providerID 
    }));
    dispatch(setProvider(provider));    
  }

  // const onSelectChange = (e, fieldName) => {
  //   e.name = fieldName;
  //   handleChange(e);
  // };

  const onSelectItemChange = (selectedValue, fieldName) => {
    setOrderData((prevData) => ({
      ...prevData,
      [fieldName]: selectedValue,
    }));
  };

  const closeOrderModal = () => {
    if (Object.keys(editOrder).length > 0){
      navigate(-1);
    }
    dispatch(clearProvider());
    dispatch(resetUploadedFeatures());
    dispatch(resetCoordinatesGeometry());
    dispatch(resetSelectedFeature());
    dispatch(cleanUpDraw());
    dispatch(resetDisplayView());
    dispatch(resetMode());
  };

  const placeOrderSuccess = () => {
    closeOrderModal();
    dispatch(setSuccessMessage("Order Placed Successfully"));
    setTimeout(() => dispatch(resetSuccessMessage()), 5000);
  };

  const placeOrder = async () => {
    const transformedData = transformOrderData(orderData);
     // If we are editing an order → delete it first
  if (editOrder.length > 0) {
    await dispatch(deleteDraftOrder(editOrder.providerTaskID));
    dispatch(resetEditOrder())
  }
    dispatch(createOrders(transformedData, placeOrderSuccess));
  };

  const showFeaturesBar = () => {
    return !isFileUploaded() &&
      orderCreationStage.details;
  };

  // const isGeoDataArchiveOrder = () => {
  //   return orderData.orderType === "Geodata Archive";
  // };

  // const isStandardOrder = () => {
  //   return orderData.orderType === "Standard Order";
  // };

  const removeFile = () => {
    dispatch(resetUploadedFeatures());
    dispatch(resetMode());
  };

  const showAoiMessage = () => {
    return (
      mode.length === 0 ||
      mode === 'delete_aoi' ||
      (mode === 'upload_file' && !isFileUploaded()) ||
      (mode !== 'upload_file' && Object.keys(geometry).length === 0)
    );
  };  

  const showRequiredMessage = () => {
    if (orderData?.name?.length === 0) {
      return true;
    }
    return false;
  }

  const disableNextButton = () => {
    if (orderCreationStage.details) {
      return showAoiMessage() || showRequiredMessage();
    } else if (orderData.provider && orderCreationStage.type) {
      return orderData.orderType.length === 0;
    }
    if (!orderData.collections) {
      return true;
    }
    return false;
  };

  const goToNextPage = () => {
    if (orderCreationStage.details) {
      setOrderCreationStage({ details: false, type: true, summary: false });
    } else if (orderCreationStage.type) {
      setOrderCreationStage({ details: false, type: false, summary: true });
      dispatch(resetHighlightOrbitpath());
    }
  };

  const goToPreviousPage = () => {
    if (orderCreationStage.summary) {
      setOrderCreationStage({ details: false, type: true, summary: false });
    } else if (orderCreationStage.type) {
      dispatch(clearProvider());
      setOrderCreationStage({ details: true, type: false, summary: false });
    } else if (orderCreationStage.summary) {
      dispatch(clearProvider());
    }
  };

  const fromOrderCreator = true;

  return (
    <>
      <div className="mainView">
        {showFeaturesBar() && <FeaturesBar props={{ ...props, fromOrderCreator }} />}
        {!orderCreationStage.summary && providerConstellations.length > 0 && <ConstellationTimeline cloudCoverMax={orderData.cloudCoverMax} />}
        {!orderCreationStage.summary && showOrbit && showOrbit.errorMessage && <ErrorMessage/>}
        <div className="orderCreationModal">
          <div className="orderCreationModalHeader bottomBorder">
            <div className="orderCreationModalHeaderText">Order</div>
            <button onClick={closeOrderModal}>
              <Close />
            </button>
            <button onClick={() => setIsModalMinimized(!isModalMinimized)} className="minimizeIcon">
              {!isModalMinimized && <MinimizeIcon />}
              {isModalMinimized && <MaximizeIcon />}
            </button>
          </div>
          {!isModalMinimized && (
            <>
              <div className={orderCreationStage.details ? "activeSectionModal" : "inActiveSectionModal"}>
                <div className="sectionDivLine">
                  <button onClick={() => setOrderCreationStage({ details: true, type: false, summary: false })} className="dropdownIconWhite">
                  {orderCreationStage.details ?
                        <DropdownWhiteOpen/> :
                        <DropdownWhiteClose/>
                      }
                  </button>
                  <div className="sectionHeaderText">
                    Details
                    {!orderCreationStage.details &&
                      <BrightGreenTickIcon className="brightGreenTickIcon" />}
                  </div>
                </div>
                {orderCreationStage.details && (
                  <>
                    {showRequiredMessage() &&
                      <div className="orderNotifications orangeShade">
                        Order name is required field
                      </div>
                    }
                    <div className="modalSection" style={{ display: "flex", paddingBottom: "0px" }}>
                      <div className="modalInputTitleWhite orderInputFields">
                        Name
                      </div>
                      <input className="modalInputFieldFullLen modalInputFieldHalfLen"
                        name='name'
                        value={orderData.name}
                        placeholder="<Order Title>"
                        onChange={handleChange}
                        autoComplete="off"
                        maxLength={30}
                      />
                    </div>
                    {/* <div className="modalSection" style={{ display: "flex", paddingBottom: "0px" }}>
                      <div className="modalInputTitleWhite orderInputFields">AOI</div>
                      <input className="modalInputFieldFullLen modalInputFieldHalfLen"
                        name='aoiLabel'
                        value={orderData.aoiLabel}
                        placeholder="<AOI Label>"
                        onChange={handleChange}
                        autoComplete="off"
                        maxLength={30}
                      />
                    </div> */}
                    {isFileUploaded() &&
                      <div className="modalSection" style={{ display: "flex", paddingBottom: "0px" }}>
                        <div className="modalInputTitleWhite orderInputFields" >
                          File Name
                        </div>
                        (<div className="modalInputFieldFullLen modalInputFieldHalfLen filenameField">
                          <span className="fileIcon"><FileSvg /></span>
                          {uploadedFilename}
                          <button className="removeFile" onClick={removeFile}>
                            <Close />
                          </button>
                        </div>)
                      </div>
                    }
                    {showAoiMessage() && <div className="orderNotifications blueShade">
                      <HelpCircleIcon />
                      Use tools on the right to create a region or upload the shape file for AOI
                    </div>}
                  </>
                )}
              </div>

              <div className={orderCreationStage.type ? "activeSectionModal" : "inActiveSectionModal"}>
                <div className="sectionDivLine topBorder">
                  <button onClick={() => setOrderCreationStage({ details: false, type: true, summary: false })} className="dropdownIconWhite">
                  {orderCreationStage.type ? <DropdownWhiteOpen/> : <DropdownWhiteClose/>}
                  </button>
                  <div className="sectionHeaderText">
                    Imaging Requirements
                    {orderCreationStage.summary &&
                      <BrightGreenTickIcon className="brightGreenTickIcon" />}
                  </div>
                </div>
                {orderCreationStage.type && (
                  <div className="modalScrollableSection">
                    <>
                      <div className="modalSection modalDivLine modalInputTitleWhite noLeftMargin subHeader summaryContent">
                        <div className="modalInputTitleWhite" style={{ fontSize: "14px" }}>
                          Resolution
                        </div>
                        <SingleSelectDropdown
                          options={orderOptions.resolution}
                          onSelectItemChange={onSelectItemChange}
                          selectedValue={orderData.resolution}
                          dropdownName="resolution"
                          placeholder="Select a resolution"
                        />
                      </div>
                      <div className="modalSection modalDivLine modalInputTitleWhite subHeader summaryContent">
                        <div className="modalInputTitleWhite">Priority Lane</div>
                        {/* <div className="modalDivLine">
                          {orderOptions.priorityLane.map((item, i) => (
                            <div className="modalInputBox" key={i}>
                              <button name="priorityLane"
                                onClick={(e) => handleChange(e, item)}
                                className={orderData.priorityLane === item ? "modalInputCircularButton active" : "modalInputCircularButton"}
                              />
                              <div className="modalInputBoxText">{item}</div>
                            </div>
                          ))}
                        </div> */}
                        <SingleSelectDropdown
                          options={orderOptions.priorityLane}
                          onSelectItemChange={onSelectItemChange}
                          selectedValue={orderData.priorityLane}
                          dropdownName="priorityLane"
                          placeholder="Select a priorityLane"
                        />
                      </div>
                      <div className="modalSection modalDivLine modalInputTitleWhite subHeader summaryContent">
                        <div className="modalInputTitleWhite">Recurring Order</div>
                        {/* <div className="modalDivLine">
                          {orderOptions.RecurringOrder.map((item, i) => (
                            <div className="modalInputBox" key={i}>
                              <button name="selectedRecurringType"
                                onClick={(e) => handleChange(e, item)}
                                className={orderData.selectedRecurringType === item ? "modalInputCircularButton active" : "modalInputCircularButton"}
                              />
                              <div className="modalInputBoxText">{item}</div>
                            </div>
                          ))}
                        </div> */}
                        <SingleSelectDropdown
                          options={orderOptions.recurringType}
                          onSelectItemChange={onSelectItemChange}
                          selectedValue={orderData.selectedRecurringType}
                          keyName={'value'}
                          valueName={'label'}
                          dropdownName="selectedRecurringType"
                          placeholder="Select a priorityLane"
                        />
                      </div>
                      <div className="modalSection">
                        <div className="modalInputTitleWhite">Imaging Spectrum</div>
                        <div className="modalDivLine">
                          {orderOptions.imagingSpectrum.map((item, i) => (
                            <div className="modalInputBox" key={i}>
                              <button name="imagingSpectrum"
                                onClick={(e) => handleChange(e, item.label)}
                                className={orderData.imagingSpectrum === item.label ? "modalInputCircularButton active" : "modalInputCircularButton"}
                              />
                              <div className="modalInputBoxText">{item.label}</div>
                            </div>
                          ))}
                        </div>
                        <div className="modalDivLine noBottomPadding">
                          <button name="isAdvancedDetails"
                            className={orderData.isAdvancedDetails ? "modalGreenTransparentBtn active" : "modalGreenTransparentBtn"}
                            onClick={(e) => handleChange(e, !orderData.isAdvancedDetails)}
                          >
                            Advanced details
                          </button>
                        </div>
                        {orderData.isAdvancedDetails && (
                          <>
                            <div className="modalDivLine">
                              <div className="modalInputTitleWhite noLeftMargin">Acquisition Requirement</div>
                            </div>

                            <div className="modalDivLine">
                              <div className="modalInputTitleWhite noLeftMargin">GSD</div>
                            </div>
                            <div className="modalDivLine modalInputFieldAlign">
                              <div className="modalInputFieldText">
                                <div className="modalInputBoxText">Min</div>
                                <input
                                  name="gsdMin"
                                  type="number"
                                  min={0}
                                  value={orderData.gsdMin}
                                  className="modalInputNumField"
                                  placeholder="in m"
                                  onChange={handleChange}
                                />
                              </div>
                              <div className="modalInputFieldText" style={{ opacity: "0.5" }}>
                                To
                              </div>
                              <div className="modalInputFieldText">
                                <div className="modalInputBoxText">Max</div>
                                <input name="gsdMax"
                                  type="number"
                                  min={0}
                                  value={orderData.gsdMax}
                                  className="modalInputNumField"
                                  placeholder="in m"
                                  onChange={handleChange}
                                />
                              </div>
                            </div>

                            {/* <div className="modalDivLine">
                              <div className="modalInputTitleWhite noLeftMargin">Nadir Angle</div>
                            </div>
                            <div className="modalDivLine modalInputFieldAlign">
                              <div className="modalInputFieldText">
                                <div className="modalInputBoxText">Min</div>
                                <input name="nadirMin"
                                  type="number"
                                  min={0}
                                  value={orderData.nadirMin}
                                  className="modalInputNumField"
                                  placeholder="in deg"
                                  onChange={handleChange}
                                />
                              </div>
                              <div className="modalInputFieldText" style={{ opacity: "0.5" }}>
                                To
                              </div>
                              <div className="modalInputFieldText">
                                <div className="modalInputBoxText">Max</div>
                                <input name="nadirMax"
                                  type="number"
                                  min={0}
                                  value={orderData.nadirMax}
                                  className="modalInputNumField"
                                  placeholder="in deg"
                                  onChange={handleChange}
                                />
                              </div>
                            </div> */}

                            <div className="modalDivLine">
                              <div className="modalInputTitleWhite noLeftMargin">Sun Angle</div>
                            </div>
                            <div className="modalDivLine modalInputFieldAlign">
                              <div className="modalInputFieldText">
                                <div className="modalInputBoxText">Min</div>
                                <input name="sunAngleMin"
                                  type="number"
                                  min={0}
                                  value={orderData.sunAngleMin}
                                  className="modalInputNumField"
                                  placeholder="in deg"
                                  onChange={handleChange}
                                />
                              </div>
                              <div className="modalInputFieldText" style={{ opacity: "0.5" }}>
                                To
                              </div>
                              <div className="modalInputFieldText">
                                <div className="modalInputBoxText">Max</div>
                                <input name="sunAngleMax"
                                  type="number"
                                  min={0}
                                  value={orderData.sunAngleMax}
                                  className="modalInputNumField"
                                  placeholder="in deg"
                                  onChange={handleChange}
                                />
                              </div>
                            </div>

                            <div className="summaryContent">
                              <div className="modalDivLine">
                                <div className="modalInputTitleWhite noLeftMargin">Max Cloud Coverage (%)</div>
                              </div>
                              <div className="noBottomPadding paddingAround">
                                <div className="modalInputFieldText">
                                  <input name="cloudCoverMax"
                                    type="number"
                                    min={0}
                                    value={orderData.cloudCoverMax}
                                    className="modalInputNumField"
                                    placeholder="in %"
                                    onChange={handleChange}
                                  />
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="modalSection summaryContent border-0">
                        <div className="modalInputTitleWhite">Opportunity Analysis</div>
                        {loading && <div className="me-2 d-flex"><Loader inverse /></div>}
                        {!loading && filteredProviderConstellations.length === 0 && (<button className="modalAnalysisButton" onClick={runAnalysisSimulation} > Run Analysis </button>)}
                      </div>
                      {filteredProviderConstellations.length > 0 &&
                        <div className="providerAnalysis-container">
                          {filteredProviderConstellations.map((provider) => (
                            <div className={`providerBox modalInputTitleWhite ${showOrbit.providerID === provider.providerID ? 'selected-provider' : ''}`} key={provider.providerID} 
                              onClick={() => {
                                showConstellation(provider);
                                dispatch(resetHighlightOrbitpath());
                              }}>
                              <div>
                                {provider.providerName}
                              </div>
                              <div>
                                <div>{provider.totalPasses}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      }
                      {/* <div className="modalSection">
                        <div className="modalInputTitleWhite">Coverage Window</div>
                        <div className="modalDivLine noBottomPadding">
                          <div className="modalInputCoverageFeild">
                            <input name="coverageWindowValue"
                              type="number"
                              min={0}
                              value={orderData.coverageWindowValue}
                              className="modalInputValField"
                              onChange={handleChange}
                            />
                            <Dropdown
                              className="reactDropdownRootClass halfWidth contents"
                              controlClassName="reactDropdownClass halfWidth"
                              options={orderOptions.coverageWindowValueType}
                              value={orderData.coverageWindowValueType}
                              menuClassName="reactDropdownMenuClass halfWidth"
                              arrowClassName="reactDropdownArrowClassName"
                              placeholder="Select an option"
                              name="coverageWindowValueType"
                              onChange={(e) => onSelectChange(e, 'coverageWindowValueType')}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="modalSection">
                        <div className="modalDivLine noBottomPadding">
                          <div className="modalInputBox">
                            <button className="modalInputRoundedBoxButton"
                              name="isRecurring"
                              onClick={(e) => handleChange(e, !orderData.isRecurring)}
                            />
                            {orderData.isRecurring &&
                              <img className="singleTickIcon"
                                src={require("./../../assets/images/svgs/SingletickIcon.svg").default}
                                alt="single-tick" />}
                            <div className={!orderData.isRecurring ? "modalInputBoxText" : "modalInputBoxText noPadding"}>Recurring</div>

                          </div>
                          {orderData.isRecurring && (
                            <>
                              <div className="">
                                <Dropdown
                                  className="reactDropdownRootClass halfWidth contents"
                                  controlClassName="reactDropdownClass halfWidth"
                                  options={orderOptions.recurringType}
                                  value={orderData.selectedRecurringType}
                                  menuClassName="reactDropdownMenuClass halfWidth"
                                  arrowClassName="reactDropdownArrowClassName"
                                  placeholder="Select an option"
                                  name="selectedRecurringType"
                                  onChange={(e) => onSelectChange(e, 'selectedRecurringType')}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div> */}
                    </>
                  </div>
                )}
              </div>

              <div className={orderCreationStage.summary ? "activeSectionModal" : "inActiveSectionModal"}>
                <div className="sectionDivLine topBorder">
                  <button onClick={() => setOrderCreationStage({ details: false, type: false, summary: true })} className="dropdownIconWhite">
                      {orderCreationStage.summary ?
                        <DropdownWhiteOpen/> :
                        <DropdownWhiteClose/>
                      }
                  </button>
                  <div className="sectionHeaderText">Order Summary</div>
                </div>
                {orderCreationStage.summary && (
                  <>
                    <div className="modalSection modalScrollableSection">
                      <div className="modalInputTitleWhite">Details</div>
                      <div className="orderDetailsBox">
                        <div className="orderDetailsBoxItem">
                          <div className="orderDetailsBoxLabel">Order Title</div>
                          <div className="orderDetailsBoxLabel orderDetailsBoxValue">{orderData.name}</div>
                        </div>
                        <div className="orderDetailsBoxItem" >
                          <div className="orderDetailsBoxLabel">Imaging Spectrum</div>
                          <div className="orderDetailsBoxLabel orderDetailsBoxValue">{orderData.imagingSpectrum}</div>
                        </div>
                        <div className="orderDetailsBoxItem" >
                          <div className="orderDetailsBoxLabel">Priority Lane</div>
                          <div className="orderDetailsBoxLabel orderDetailsBoxValue">{orderData.priorityLane}</div>
                        </div>
                        <div className="orderDetailsBoxItem" >
                          <div className="orderDetailsBoxLabel">Geodata Provider</div>
                          <div className="orderDetailsBoxLabel orderDetailsBoxValue">{provider.find(opt => opt.providerID === orderData.provider)?.name || orderData.provider}
                          </div>
                        </div>
                      </div>
                      <div className="modalDivLine modalInputTitleWhite noLeftMargin subHeader summaryContent">
                        <span>Target Collection</span>
                        <SingleSelectDropdown
                          options={collections}
                          onSelectItemChange={onSelectItemChange}
                          keyName="collectionID"
                          valueName="name"
                          selectedValue={orderData.collections}
                          dropdownName="collections"
                          placeholder="Select a collection"
                        />
                      </div>

                      {/* <div className="modalDivLine modalInputTitleWhite noLeftMargin subHeader summaryContent">
                        <span>Delivery Location</span>
                        <SingleSelectDropdown
                          options={storageLocation}
                          onSelectItemChange={onSelectItemChange}
                          keyName="storageEndpointID"
                          valueName="name"
                          selectedValue={orderData.storageLocation}
                          dropdownName="storageLocation"
                          placeholder="Select a location"
                        />
                      </div> */}

                      <div className="orderNotifications orangeShade" style={{ fontSize: "10px", marginTop: "12px" }}>
                        Do you want to place order for the selected images?
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          <div className="orderCreationModalFooter topBorder">
            <button className={!orderCreationStage.details ?
              "modalFooterCloseBtn" : "modalFooterCloseBtn disabledBtn"}
              onClick={() => goToPreviousPage()}>
              Back
            </button>
            {!orderCreationStage.summary ?
              (
                <button
                  className={disableNextButton() ?
                    "modalFooterApplyBtn disabledBtn" : "modalFooterApplyBtn"}
                  disabled={disableNextButton()}
                  onClick={() => goToNextPage()}>
                  Next
                </button>
              ) :
              (
                <button className={disableNextButton() ?
                  "modalFooterApplyBtn disabledBtn" : "modalFooterApplyBtn"} disabled={disableNextButton()} onClick={() => placeOrder()}>
                  Place Order
                </button>
              )
            }
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderCreation;
